import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { saveAs } from 'file-saver';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { getFile, postFile } from 'services/Http';

const UploadNotes = [
    {
        title: 'Generate File Tips',
        notes: [
            'When providing a template to a supplier, download a current version to ensure that it includes the most up to date list of products.',
            "It doesn't matter which supplier you use to generate this file, as the same file is created for any supplier.",
            'The template includes all products configured for any payer under the pharmacy benefit. For medical benefit drugs, please have the pharmacy enter templates via the application.',
        ],
    },
    {
        title: 'File Completion Tips',
        notes: [
            'Health Plan is a required free text field and can contain a specific payer segment name or "All Health Plans". The text string must match the payer segment name in the system. Please QA prior to upload and clarify any uncertainty with the pharmacy.',
            'Prescriber and State List fields are optional. If either value is populated for a list, please QA prior to upload and clarify any uncertainty with the pharmacy.',
            'Product rows are optional. Any row with both Discount Amount and Start Date left blank will be ignored.',
            'Discount Amounts are AWP. For AWP - 20 the user would simply enter "-20".',
            'End Date is pre-populated and may be updated.',
            'Product Templates will be created for each product that has a Discount Amount and Start/End Dates populated.',
            'The generated template includes 5 identical sheets. Additional sheets may be added as needed.',
        ],
    },
    {
        title: 'File Upload Tips',
        notes: [
            'Discount Rates and Start/End Dates on existing Product Templates can be updated by uploading a file with new values.',
            'Uploading a file for new Product Templates does not remove any existing Product Templates.',
        ],
    },
];

export const AutomationsUpload = ({ onSecondaryButtonClick = undefined }) => {
    const { npi } = useParams();
    const [isGeneratingFile, setIsGeneratingFile] = useState(false);
    const generateFile = async () => {
        try {
            setIsGeneratingFile(true);
            saveAs(
                await getFile('/auctions/admin/prebids/bulk_upload/template'),
                'ProductTemplateRules.xlsx',
            );
        } catch (err) {
            handleError('An error occurred generating file. Please try again.', err);
        } finally {
            setIsGeneratingFile(false);
        }
    };

    return (
        <Formik
            initialValues={{}}
            onSubmit={async (values, { setFieldValue, resetForm }) => {
                try {
                    const validationErrors = await postFile(
                        `/auctions/admin/prebids/bulk_upload?pharmacy_name=${values.pharmacyApproverName}&pharmacy_email=${values.pharmacyApproverEmail}`,
                        values.fileToUpload,
                        undefined,
                        { 'x-npi': npi },
                    );

                    if (
                        validationErrors &&
                        validationErrors?.message !== 'success' &&
                        Object.keys(validationErrors).length > 0
                    ) {
                        handleError('File not uploaded. Invalid values found in file.');
                        await setFieldValue('validationErrors', validationErrors);
                    } else {
                        SuccessToast({ message: 'File successfully uploaded.' });
                        resetForm();
                    }
                } catch (err) {
                    handleError('An error occurred uploading file. Please try again.', err);
                }
            }}
            validationSchema={Yup.object({
                pharmacyApproverName: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                pharmacyApproverEmail: Yup.string()
                    .email()
                    .required(validationStrings.requiredField.errorMessage),
                productTemplateFile: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .length(1, validationStrings.requiredField.errorMessage),
            })}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Automation Rules"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !formik.isValid || !formik.dirty}
                    onSecondaryButtonClick={onSecondaryButtonClick ?? formik.resetForm}
                    isLoading={formik.isSubmitting}
                    formHeaderRightContent={
                        <>
                            <FullscreenLoading
                                isShowing={isGeneratingFile}
                                loadingText="Generating File..."
                            />
                            <ActionButtonText
                                label="Generate File"
                                leftIcon={IconNames.IMPORT}
                                onClick={generateFile}
                            />
                        </>
                    }
                    formLeftContent={
                        <>
                            <div className="sds_bulk-upload__section-title">Pharmacy Approver</div>
                            <TextInputFormik id="pharmacyApproverName" label="Name" required />
                            <TextInputFormik id="pharmacyApproverEmail" label="Email" required />
                            <div className="sds_bulk-upload__section-title">File Upload</div>
                            {formik.values.validationErrors &&
                                Object.entries(formik.values.validationErrors).map(
                                    ([key, invalidValues]) => (
                                        <div key={key} className="margin__bottom--medium">
                                            <InlineMessage
                                                intent={InlineMessage.Intent.ERROR}
                                                title={key}
                                                label={`"${invalidValues.join(
                                                    '", "',
                                                )}" is invalid. Please fix the error in the uploaded file.`}
                                            />
                                        </div>
                                    ),
                                )}
                            <FileUploadFormik
                                id="productTemplateFile"
                                label="Product Template File"
                                required
                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                maxAllowedFiles={1}
                                onAddFiles={(files) =>
                                    formik.setFieldValue('fileToUpload', files?.[0])
                                }
                                onRemoveFile={() => formik.setFieldValue('fileToUpload', undefined)}
                            />
                        </>
                    }
                    formRightContent={UploadNotes.map((note) => (
                        <div key={note.title} className="sds_bulk-upload__notes">
                            <InlineMessage
                                intent={InlineMessage.Intent.TIP}
                                title={note.title}
                                wrapText
                                label={
                                    <ul>
                                        {note.notes.map((bullet) => (
                                            <li key={bullet}>{bullet}</li>
                                        ))}
                                    </ul>
                                }
                            />
                        </div>
                    ))}
                />
            )}
        </Formik>
    );
};

AutomationsUpload.propTypes = {
    onSecondaryButtonClick: PropTypes.func,
};
